import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import debounce from 'lodash-es/debounce';
import React, { useCallback, useState } from 'react';

import useSyndicatedSegments, {
  SyndicatedSegment,
  useResolveExternalSegment,
  useSyndicatedSegmentFacets,
} from '../../../hooks/useSyndicatedSegments';

interface SyndicatedSegmentsModalProps {
  onSelect: (segmentId: number | string, name: string) => void;
  onClose: () => void;
}

function formatCpm(cpm: string | null): string {
  if (!cpm) return '-';
  const num = parseFloat(cpm);
  if (Number.isNaN(num)) return '-';
  return `$${num.toFixed(2)}`;
}

// getRequestJSON rejects with the raw response body, which is our controller's
// { errors: "..." } payload for 400/404.
function resolveErrorMessage(error: Error): string {
  try {
    const parsed = JSON.parse(error.message);
    if (parsed?.errors) return String(parsed.errors);
  } catch {
    // fall through to the raw message
  }
  return error.message || 'Could not resolve this segment.';
}

function SyndicatedSegmentsModal({ onSelect, onClose }: SyndicatedSegmentsModalProps) {
  const [q, setQ] = useState('');
  const [debouncedQ, setDebouncedQ] = useState('');
  const [provider, setProvider] = useState('');
  const [subCat1, setSubCat1] = useState('');
  const [subCat2, setSubCat2] = useState('');
  const [subCat3, setSubCat3] = useState('');
  const [resolvingExternalId, setResolvingExternalId] = useState<string | null>(null);
  const [resolveError, setResolveError] = useState<string | null>(null);

  const { data: facets } = useSyndicatedSegmentFacets();
  const resolveExternalSegment = useResolveExternalSegment();

  const filters = {
    q: debouncedQ || undefined,
    provider: provider || undefined,
    sub_category_1: subCat1 || undefined,
    sub_category_2: subCat2 || undefined,
    sub_category_3: subCat3 || undefined,
  };

  const { data, isLoading, page, setPage, perPage } = useSyndicatedSegments(filters);

  const collection = data?.collection || [];
  const totalItems = data?.pagination?.total_items || 0;

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedSetQ = useCallback(debounce((val: string) => setDebouncedQ(val), 400), []);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setQ(e.target.value);
    debouncedSetQ(e.target.value);
    setPage(1);
  };

  const handleFilterChange = (setter: (v: string) => void) => (e: SelectChangeEvent<string>) => {
    setter(e.target.value);
    setPage(1);
  };

  // The row only knows its external identity (im_0001 + source), so hand that to
  // the backend and attach whatever ventura segment it resolves to.
  const handleRowClick = (segment: SyndicatedSegment) => {
    if (resolvingExternalId) return;

    setResolveError(null);
    setResolvingExternalId(segment.external_id);

    resolveExternalSegment.mutate(
      { externalSource: segment.external_source, externalId: segment.external_id },
      {
        onSuccess: (resolved) => {
          setResolvingExternalId(null);
          const segmentId = resolved?.segment_id ?? resolved?.id;
          if (segmentId === null || segmentId === undefined) {
            setResolveError(`No ventura segment is linked to "${segment.segment_name}" yet.`);
            return;
          }
          onSelect(segmentId, resolved.name || segment.segment_name);
        },
        onError: (error) => {
          setResolvingExternalId(null);
          setResolveError(`${segment.segment_name}: ${resolveErrorMessage(error)}`);
        },
      }
    );
  };

  const showSubCat1 = (facets?.sub_category_1?.length ?? 0) > 0;
  const showSubCat2 = (facets?.sub_category_2?.length ?? 0) > 0;
  const showSubCat3 = (facets?.sub_category_3?.length ?? 0) > 0;

  return (
    <Stack p={3} spacing={2} sx={{ minWidth: 700 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography variant="h6">Add Syndicated Segment</Typography>
        <Button variant="outlined" onClick={onClose}>
          Close
        </Button>
      </Stack>

      <Stack direction="row" spacing={2} sx={{ flexWrap: 'wrap', gap: 1 }}>
        <TextField
          label="Search"
          size="small"
          value={q}
          onChange={handleSearchChange}
          placeholder="Search segments..."
          sx={{ minWidth: 200 }}
        />

        {(facets?.providers?.length ?? 0) > 0 && (
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Provider</InputLabel>
            <Select value={provider} label="Provider" onChange={handleFilterChange(setProvider)}>
              <MenuItem value="">All</MenuItem>
              {facets.providers.map((p) => (
                <MenuItem key={p} value={p}>
                  {p}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        {showSubCat1 && (
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Subcategory 1</InputLabel>
            <Select value={subCat1} label="Subcategory 1" onChange={handleFilterChange(setSubCat1)}>
              <MenuItem value="">All</MenuItem>
              {facets.sub_category_1.map((v) => (
                <MenuItem key={v} value={v}>
                  {v}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        {showSubCat2 && (
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Subcategory 2</InputLabel>
            <Select value={subCat2} label="Subcategory 2" onChange={handleFilterChange(setSubCat2)}>
              <MenuItem value="">All</MenuItem>
              {facets.sub_category_2.map((v) => (
                <MenuItem key={v} value={v}>
                  {v}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        {showSubCat3 && (
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Subcategory 3</InputLabel>
            <Select value={subCat3} label="Subcategory 3" onChange={handleFilterChange(setSubCat3)}>
              <MenuItem value="">All</MenuItem>
              {facets.sub_category_3.map((v) => (
                <MenuItem key={v} value={v}>
                  {v}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
      </Stack>

      {resolveError && (
        <Alert severity="error" onClose={() => setResolveError(null)}>
          {resolveError}
        </Alert>
      )}

      {isLoading ? (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <TableContainer sx={{ maxHeight: 400 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>ID</TableCell>
                  <TableCell>Segment Name</TableCell>
                  <TableCell>Provider</TableCell>
                  <TableCell>Category</TableCell>
                  <TableCell>Subcategory 1</TableCell>
                  <TableCell>Subcategory 2</TableCell>
                  <TableCell>CPM</TableCell>
                  <TableCell width={40} />
                </TableRow>
              </TableHead>
              <TableBody>
                {collection.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8}>
                      <Typography color="text.secondary" align="center" py={2}>
                        No segments found
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  collection.map((segment) => (
                    <TableRow
                      key={`${segment.external_source}-${segment.external_id}`}
                      hover
                      onClick={() => handleRowClick(segment)}
                      sx={{
                        cursor: resolvingExternalId ? 'default' : 'pointer',
                        opacity: resolvingExternalId && resolvingExternalId !== segment.external_id ? 0.5 : 1,
                      }}
                    >
                      <TableCell>{segment.external_id}</TableCell>
                      <TableCell>{segment.segment_name}</TableCell>
                      <TableCell>{segment.provider}</TableCell>
                      <TableCell>{segment.category}</TableCell>
                      <TableCell>{segment.sub_category_1 ?? '-'}</TableCell>
                      <TableCell>{segment.sub_category_2 ?? '-'}</TableCell>
                      <TableCell>{formatCpm(segment.cpm)}</TableCell>
                      <TableCell>
                        {resolvingExternalId === segment.external_id && <CircularProgress size={16} />}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            component="div"
            count={totalItems}
            page={page - 1}
            onPageChange={(_e, newPage) => setPage(newPage + 1)}
            rowsPerPage={perPage}
            rowsPerPageOptions={[perPage]}
          />
        </>
      )}
    </Stack>
  );
}

export default SyndicatedSegmentsModal;
